import numeral from 'numeral';

interface NumericValueProps {
    value: number | string;
    showPercentage?: boolean;
    showSign?: boolean;
    showColor?: boolean; // Control whether to show red/green colors
    className?: string;
    precision?: number; // Optional precision parameter
}

export default function NumericValue({
    value,
    showPercentage = false,
    showSign = false,
    showColor = true, // Default to true for backward compatibility
    className = '',
    precision,
}: NumericValueProps) {
    // Convert to number using numeral for better precision handling
    const numericValue =
        typeof value === 'string' ? numeral(value).value() : value;

    // Check if it's a valid number
    if (
        numericValue === null ||
        numericValue === undefined ||
        isNaN(numericValue)
    ) {
        return <div className={`text-gray-500 ${className}`}>--</div>;
    }

    // Determine if positive or negative
    const isPositive = numericValue > 0;
    const isNegative = numericValue < 0;
    const isZero = numericValue === 0;

    // Get color classes
    const getColorClass = () => {
        if (!showColor) return ''; // No color when showColor is false
        if (isPositive) return 'text-error'; // Green for positive
        if (isNegative) return 'text-success'; // Red for negative
        return '';
    };

    // Format the display value using numeral
    const formatValue = () => {
        let displayValue = Math.abs(numericValue);

        // Add percentage symbol if needed
        if (showPercentage) {
            // Use numeral for precise multiplication to avoid floating point precision issues
            displayValue = Number(
                (numeral(displayValue).multiply(100).value() || 0).toFixed(2),
            );
        }

        // Use numeral for precise formatting
        const formatted =
            precision !== undefined
                ? numeral(displayValue).format(`0.${'0'.repeat(precision)}`)
                : numeral(displayValue).format('0,0.[000000000000000000]');

        // Add percentage symbol
        if (showPercentage) {
            return `${formatted}%`;
        }

        return formatted;
    };

    // Get sign symbol
    const getSign = () => {
        if (!showSign) return '';
        if (isPositive) return '+';
        if (isNegative) return '-';
        return '';
    };

    return (
        <div className={`${getColorClass()} ${className}`}>
            {getSign()}
            {formatValue()}
        </div>
    );
}
